import {
  toFormValues,
  type LightweightAutomationFormValues,
} from './automation-form-model';
import { createDefaultAutomationRecord } from './automation-domain';

export type AutomationTemplateKey =
  | 'blank'
  | 'research_news_digest'
  | 'company_watch'
  | 'person_research'
  | 'site_monitor';

export type AutomationTemplateDefinition = {
  key: AutomationTemplateKey;
  label: string;
  description: string;
  build: (base: LightweightAutomationFormValues) => LightweightAutomationFormValues;
};

export const AUTOMATION_TEMPLATES: readonly AutomationTemplateDefinition[] = [
  {
    key: 'blank',
    label: 'Blank',
    description: 'Start from a minimal automation shell and write the task in your own words.',
    build: (base) => base,
  },
  {
    key: 'research_news_digest',
    label: 'News Scan',
    description: 'A starting point for recurring news monitoring with inbox message delivery.',
    build: (base) => ({
      ...base,
      name: 'News Scan',
      moduleKey: 'inbox_digest',
      instructionsMarkdown: [
        'Research the latest logistics news from the last 24 hours.',
        'Prioritize relevant Romanian and European logistics coverage.',
        'Return a concise title and a complete Markdown message in content.',
        'Use bullets with source links and end with a short practical takeaway.',
      ].join('\n'),
      deliveryMode: 'message',
      digestDestination: 'inbox',
    }),
  },
  {
    key: 'company_watch',
    label: 'Company Watch',
    description: 'A starting point for tracking a company and returning sourced updates.',
    build: (base) => ({
      ...base,
      name: 'Company Watch',
      moduleKey: 'research',
      instructionsMarkdown: [
        'Research meaningful recent public updates about the specified company.',
        'Prioritize official sources and reputable coverage.',
        'Return a structured payload with an items array.',
        'Each item should include title, summary, url, date, company, and topic when relevant.',
      ].join('\n'),
    }),
  },
  {
    key: 'person_research',
    label: 'Person Scan',
    description: 'A starting point for public-information research on a person.',
    build: (base) => ({
      ...base,
      name: 'Person Scan',
      moduleKey: 'research',
      instructionsMarkdown: [
        'Research recent public information about the target person.',
        'Keep the result factual and sourced.',
        'Return a structured payload with an items array.',
        'Each item should include title, summary, url, date, and topic when relevant.',
      ].join('\n'),
    }),
  },
  {
    key: 'site_monitor',
    label: 'Site Monitor',
    description: 'A starting point for watching a known site and surfacing new content.',
    build: (base) => ({
      ...base,
      name: 'Site Monitor',
      moduleKey: 'research',
      instructionsMarkdown: [
        'Monitor the specified site for newly published relevant content.',
        'Fetch the site directly when needed and prefer first-party pages.',
        'Return a structured payload with an items array.',
      ].join('\n'),
    }),
  },
] as const;

export function buildTemplateValues(
  tenantId: string,
  templateKey: AutomationTemplateKey,
): LightweightAutomationFormValues {
  const baseRecord = createDefaultAutomationRecord(tenantId, `automation_${Date.now()}`);
  const baseValues = toFormValues(baseRecord);
  const template =
    AUTOMATION_TEMPLATES.find((entry) => entry.key === templateKey) ?? AUTOMATION_TEMPLATES[0];
  return template.build(baseValues);
}
